<template>
  <v-dialog
    v-model="openDialog"
    width="80%"
    persistent
    id="devices_rf-recording-table-dialog"
  >
    <v-card>
      <v-card-title v-if="!device_id">
        {{ $t("modules.devices.rfRecordings.title") }}
      </v-card-title>
      <v-card-title v-else>
        {{
          $t("modules.devices.rfRecordings.recordingsForDevice", [
            this.getDeviceDetail(device_id, "name")
          ])
        }}
      </v-card-title>
      <v-card-text>
        <v-row class="mx-0 py-4">
          <v-btn outlined color="primary" @click="fetchRfRecordings">
            <v-icon>
              mdi-refresh
            </v-icon>
            {{ $t("modules.devices.rfRecordings.refresh") }}</v-btn
          >
          <v-spacer></v-spacer>
          <v-text-field
            :label="$t('modules.devices.rfRecordings.search')"
            v-model="search"
            prepend-icon="mdi-magnify"
            hide-details
            class="mr-2"
            clearable
          ></v-text-field>
          <v-menu
            ref="dateMenu"
            v-model="dateMenu"
            :close-on-content-click="false"
            :return-value.sync="dateRange"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                class="mr-2 shrink"
                height="31"
                v-model="dateRangeText"
                :label="$t('modules.logs.systemLog.dateRange')"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              color="primary"
              v-model="dateRange"
              no-title
              scrollable
              range
            >
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="dateMenu = false">
                {{ $t("actions.cancel") }}
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="$refs.dateMenu.save(dateRange)"
              >
                {{ $t("actions.ok") }}
              </v-btn>
            </v-date-picker>
          </v-menu>
          <v-btn @click="filter">{{ $t("actions.filter") }}</v-btn>
          <v-btn @click="clear" class="ml-2">{{ $t("actions.clear") }}</v-btn>
        </v-row>
        <v-card>
          <v-data-table
            id="devices_rf-recordings-table"
            ref="recordingContainer"
            v-model="selected"
            :headers="customHeaders"
            :items="loadItems"
            :loading="loading"
            :show-select="!device_id"
            color="primary"
            hide-default-footer
            item-key="report_uuid"
            must-sort
            :options.sync="options"
            :height="loadItems.length > 10 ? '528px' : 'auto'"
            class="scroll"
            fixed-header
          >
            <template
              v-if="!device_id"
              v-slot:header.data-table-select="{ props }"
            >
              <v-simple-checkbox
                color="primary"
                v-bind="props"
                label="Select"
                :ripple="false"
                @click="selectAll"
              ></v-simple-checkbox>
            </template>

            <template
              v-if="!device_id"
              v-slot:item.data-table-select="{ isSelected, select }"
            >
              <v-simple-checkbox
                color="primary"
                :value="isSelected"
                @input="select($event)"
                :ripple="false"
              ></v-simple-checkbox>
            </template>
            <template v-slot:item.index="{ item }">
              {{ recordings.indexOf(item) + 1 }}
            </template>
            <template v-slot:item.action="{ item }">
              <v-btn icon color="red" class="mr-4" v-if="false">
                <v-icon>
                  mdi-delete
                </v-icon></v-btn
              >
              <v-btn
                @click.prevent="download(item.url_to_file)"
                outlined
                color="primary"
                :disabled="
                  !isPermitted(PermissionEnum.RF_RECORDINGS.UPDATE_ALL)
                "
                >{{ $t("modules.devices.rfRecordings.downloadCsv") }}</v-btn
              >
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>
      <v-card-actions class="mx-2 pb-6">
        <div>
          <v-btn
            color="primary"
            outlined
            :loading="downloadLoading"
            @click="downloadAll(true)"
            :disabled="
              loadItems.length === 0 ||
                !isPermitted(PermissionEnum.RF_RECORDINGS.UPDATE_ALL)
            "
            >{{ $t("modules.devices.rfRecordings.downloadAll") }}</v-btn
          >
          <v-btn
            class="ml-4"
            color="primary"
            outlined
            :loading="downloadLoading"
            :disabled="
              selected.length === 0 ||
                !isPermitted(PermissionEnum.RF_RECORDINGS.UPDATE_ALL)
            "
            @click="downloadAll(false)"
            v-if="!device_id"
            >{{ $t("modules.devices.rfRecordings.downloadAllSelected") }}</v-btn
          >
        </div>
        <v-spacer></v-spacer>
        <v-btn color="secondary" @click="close">{{
          $t("actions.close")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { handleError } from "@/utils/error";
import util from "@/modules/data-path/util";
import { getDeepCopy, getFileNameFromResponseHeader } from "@/utils";
import { permissionMixin } from "@/mixins/permissions.js";

export default {
  mixins: [permissionMixin],
  data() {
    return {
      selected: [],
      loading: false,
      downloadLoading: false,
      headers: [
        {
          text: "#",
          value: "index",
          sortable: false
        },
        {
          text: this.$t("modules.devices.rfRecordings.deviceName"),
          value: "device_name",
          align: "center"
        },
        {
          text: this.$t("modules.devices.rfRecordings.sn"),
          value: "serial",
          align: "center"
        },
        {
          text: this.$t("modules.devices.rfRecordings.startTime"),
          value: "start_time",
          align: "center",
          sort: this.datetimeSort
        },
        {
          text: this.$t("modules.devices.rfRecordings.endTime"),
          value: "end_time",
          align: "center",
          sort: this.datetimeSort
        },
        {
          text: "",
          value: "action",
          align: "center",
          sortable: false
        }
      ],
      search: "",
      height: 0,
      rows: 20,
      loadItems: [],
      options: {},
      customHeaders: [],
      isScrollable: false,
      dateRange: [],
      dateMenu: false
    };
  },
  computed: {
    devices() {
      return this.$store.state.devices.devices;
    },
    openDialog: {
      get() {
        return this.$store.state.devices.rfRecordings.openDialog;
      },
      set(val) {
        this.$store.commit("devices/updateRfRecordingOpenDialog", val);
      }
    },
    recordings() {
      return getDeepCopy(this.$store.state.devices.rfRecordings.recordings)
        .map(item => {
          item.start_time = this.paserDatetime(item.start_time);
          item.end_time = this.paserDatetime(item.end_time);
          return {
            ...item,
            device_name: this.getDeviceDetail(item.device_id, "name"),
            serial: this.getDeviceDetail(item.device_id, "pairing_id")
          };
        })
        .sort((a, b) => this.datetimeSort(a.start_time, b.start_time));
    },
    device_id: {
      get() {
        return this.$store.state.devices.rfRecordings.device_id;
      },
      set(val) {
        this.$store.commit("devices/updateRfRecordingDeviceId", val);
      }
    },
    dateRangeText() {
      return this.dateRange.join("~");
    }
  },
  watch: {
    search(val) {
      if (val === "") {
        this.loadItems = this.recordings;
      }
      this.filter();
    },
    openDialog(val) {
      this.loadItems = [];
      if (!val) return;
      if (this.device_id)
        this.customHeaders = this.headers.filter(item => {
          return (
            ["index", "start_time", "end_time", "action"].indexOf(
              item.value
            ) !== -1
          );
        });
      else {
        this.customHeaders = this.headers;
      }
      this.fetchRfRecordings();
    },
    dateRange(val) {
      if (val && val.length === 2) {
        if (new Date(val[0]) > new Date(val[1])) {
          this.dateRange = [val[1], val[0]];
        }
      }
    }
  },
  updated() {
    if (this.isScrollable) return;
    const recordingTable = document.getElementById(
      "devices_rf-recordings-table"
    );
    if (recordingTable) {
      let el = recordingTable.children[0];
      el.addEventListener("scroll", this.scroll);
      this.computeHeight();
      this.isScrollable = true;
    }
  },
  beforeDestroy() {
    const rfRecordingsTableElement = document.getElementById(
      "devices_rf-recordings-table"
    );
    if (rfRecordingsTableElement)
      rfRecordingsTableElement.children[0].removeEventListener(
        "scroll",
        this.scroll
      );
    this.close();
  },
  methods: {
    selectAll() {
      if (this.selected.length !== this.loadItems.length) {
        this.selected = this.loadItems;
      } else {
        this.selected = [];
      }
    },
    scroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (this.options.itemsPerPage < this.recordings.length) {
        if (scrollTop + clientHeight >= scrollHeight - 5) {
          this.paginate();
        }
      }
    },
    computeHeight() {
      let el = document.getElementById("devices_rf-recordings-table");
      let boxOffset = el.getBoundingClientRect();
      let viewPortHeight = window.innerHeight;
      this.height = viewPortHeight - boxOffset.top;
    },
    paginate() {
      this.options.itemsPerPage = this.options.itemsPerPage + this.rows;
    },
    fetchRfRecordings() {
      this.options.itemsPerPage = this.rows;
      this.loading = true;
      this.$store
        .dispatch("devices/fetchRfRecordings", {
          device_id: this.device_id
        })
        .then(() => {
          if (this.search) {
            this.filter();
          } else {
            this.loadItems = this.recordings;
          }
        })
        .catch(error => {
          handleError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    close() {
      this.device_id = null;
      this.search = "";
      this.openDialog = false;
      this.loadItems = [];
      this.selected = [];
      this.dateRange = [];
    },
    download(url) {
      this.$store
        .dispatch("devices/checkRfRecordingUrlToFile", url)
        .then(() => {
          new Promise((resolve, reject) => {
            // Workaround for a programmatical file download.
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.setAttribute("download", "");
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            resolve();
          });
        })
        .catch(error => handleError(error));
    },
    getDeviceDetail(device_id, type) {
      const devices = this.devices.filter(item => item.id === device_id);
      if (devices.length === 0) {
        return "N/A";
      }
      return devices[0][type] || "N/A";
    },
    filter() {
      let start_date = this.parseDateTime(this.dateRange[0], 0);
      let end_date = this.dateRange[1] || this.dateRange[0];
      end_date = this.parseDateTime(end_date, 24);
      this.loadItems = this.recordings;
      if (this.search || start_date || end_date) {
        if (this.search)
          this.loadItems = this.loadItems.filter(recording =>
            this.customHeaders
              .filter(item => item.value !== "action")
              .filter(item => item.value !== "index")
              .map(item => item.value)
              .some(
                k =>
                  recording[k]
                    .toString()
                    .toLowerCase()
                    .indexOf(this.search.toLowerCase()) > -1
              )
          );
        if (start_date) {
          this.loadItems = this.loadItems
            .filter(
              recording =>
                new Date(recording.start_time) >= new Date(start_date)
            )
            .filter(
              recording => new Date(recording.start_time) <= new Date(end_date)
            );
        }
      }
    },
    parseDateTime(date, hours) {
      return new Date(date).setHours(hours, 0, 0, 0) || null;
    },
    clear() {
      this.search = "";
      this.dateRange = [];
      this.loadItems = this.recordings;
    },
    downloadAll(all = true) {
      let data = null;
      if (all) {
        data = {
          report_uuids: this.recordings.map(item => item.report_uuid),
          device_id: this.device_id
        };
      } else {
        data = {
          report_uuids: this.selected.map(item => item.report_uuid),
          device_id: this.device_id
        };
      }
      this.downloadLoading = true;
      this.$store
        .dispatch("devices/downloadRfRecordings", data)
        .then(response => {
          const filename = getFileNameFromResponseHeader(response);
          this.downloadBlobFile(response.data, filename);
        })
        .catch(error => handleError(error))
        .finally(() => (this.downloadLoading = false));
      this.selected = [];
    },
    paserDatetime(datetime) {
      return new Date(datetime).toLocaleString();
    },
    datetimeSort(a, b) {
      return new Date(b).getTime() - new Date(a).getTime();
    },
    downloadBlobFile: util.downloadBlobFile
  }
};
</script>

<style></style>
